import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { Permissions } from '@/enums/permissions'
import { useAppStore } from '@/stores/app'
import { usePollStore } from '@/stores/poll'

/**
 * Layout state shared by `Default.vue` and its components (vue-spec `structure.md` —
 * `layouts/layout.ts`). Module-level, because there is one chrome per window: two copies of
 * "is the sidebar open" is how a menu closes itself on one component and not the other.
 *
 * Dark mode is deliberately NOT here, and it is not the topbar's either. Nuxt UI auto-registers
 * VueUse's `useDark`, and `stores/app.ts` wraps that SAME state as `colorMode` — one source of
 * truth, per vue-spec `theme.md`. The topbar only renders `ColorModeToggle`; duplicating the
 * state here would be the second source of truth theme.md forbids.
 */

/**
 * THE MENU, in one place: `Sidebar.vue` renders it and nothing else describes it. It lists
 * only routes that EXIST.
 *
 * It is the legacy portal menu (`old/Skynet_ClientsWEB` `navMenuItems.js`) expressed as
 * data: same entries, same order, same permission gates. Three fields go beyond the house
 * template's model because this menu needs them:
 *
 *   - `permission` — the portal permission code that gates the entry.
 *   - `badge`      — a live count read at render time (the tasks waiting for the client's
 *     confirmation, on the Tasks group and on its "In confirmation" row).
 *   - `children`   — the legacy Tasks group, a collapsible row with the three task lists.
 *
 * `routes` is every route name of an entry, so a DETAIL page (a project, an invoice) keeps its
 * section highlighted. The labels are i18n KEYS: the strings stay in `libs/i18n/locales/*`.
 */
export interface NavItem {
    /** The i18n key of its label. */
    labelKey: string
    icon?: string
    /** The route path this entry navigates to — a real anchor. */
    to?: string
    /** The portal permission code required to see this entry. */
    permission?: number
    /** A live count shown as a badge; the badge is hidden while it is 0. */
    badge?: () => number
    /** Collapsible sub-entries. A parent with no visible child is not rendered. */
    children?: NavItem[]
    /** Every route name of this section, detail pages included — what lights the row. */
    routes?: string[]
}

/**
 * A SECTION of the menu. `labelKey` is a small mono-uppercase header above the group, or `null`
 * for a group that carries none. The portal menu is one short unheaded list.
 */
export interface NavGroup {
    labelKey: string | null
    items:    NavItem[]
}

/** The tasks waiting for the client's confirmation, polled by `stores/poll.ts`. */
const tasksInConfirmation = (): number => usePollStore().tasksInConfirmation

export const NAV_GROUPS: NavGroup[] = [
    {
        labelKey: null,
        items: [
            { labelKey: 'nav.submit_request', icon: 'i-lucide-plus', to: '/request', permission: Permissions.AddTask, routes: ['addRequest'] },

            {
                labelKey: 'nav.tasks',
                icon:     'i-lucide-list',
                badge:    tasksInConfirmation,
                children: [
                    { labelKey: 'task.list_in_confirmation', icon: 'i-lucide-mail',         to: '/task/inConfirmation', badge: tasksInConfirmation, routes: ['tasksInConfirmation'] },
                    { labelKey: 'task.list_open',            icon: 'i-lucide-list',         to: '/task/all',            routes: ['allTasks'] },
                    { labelKey: 'task.list_finished',        icon: 'i-lucide-circle-check', to: '/task/finished',       routes: ['finishedTasks'] },
                ],
            },

            { labelKey: 'nav.projects', icon: 'i-lucide-award',        to: '/projects', permission: Permissions.ViewProjects, routes: ['projects', 'project'] },
            { labelKey: 'nav.invoices', icon: 'i-lucide-dollar-sign',  to: '/invoices', permission: Permissions.ViewInvoices, routes: ['invoices', 'invoice'] },
        ],
    },
]

/**
 * IS THE MOBILE DRAWER OPEN. Module-level, and the only copy — below 1024px the sidebar is not
 * a column any more but an overlay over the whole shell, and an overlay that two components
 * disagree about is an overlay that cannot be closed.
 *
 * It only ever MEANS anything on mobile. On the desktop the sidebar is rendered because it is
 * the layout's left column, not because this is `true`, which is why `useLayoutShell()` forces
 * it back to `false` the moment the window grows past the breakpoint: a flag left set by a
 * phone-width session would otherwise re-open the drawer the next time the window narrows.
 */
const menuOpen = ref(false)

const openMenu = (): void => {
    menuOpen.value = true
}

const closeMenu = (): void => {
    menuOpen.value = false
}

const toggleMenu = (): void => {
    menuOpen.value = !menuOpen.value
}

/**
 * The state and the ONE action per verb. Every component that can open or close the drawer —
 * the topbar's hamburger, the drawer's own X, a nav row, the backdrop — calls one of these
 * three; none of them writes `menuOpen` itself.
 */
export const useLayout = () => {
    const appStore = useAppStore()

    const isMobile = computed((): boolean => appStore.isMobile())

    return { menuOpen, isMobile, openMenu, closeMenu, toggleMenu }
}

/**
 * THE DRAWER'S BEHAVIOUR, wired once by `Default.vue` and nowhere else.
 *
 * A drawer that can be opened and not closed is what the shell shipped with, so the four ways
 * out live together here rather than one per component: Escape, a route change, growing past
 * the breakpoint, and — in the layout, because it is a paint and not a behaviour — the
 * backdrop. While it is open the body cannot scroll, or the page behind the overlay scrolls
 * under the thumb instead of the menu.
 *
 * The listener is on `document` for the whole life of the shell rather than added and removed
 * around `menuOpen`: one registration, no window where a key lands between the watcher and the
 * listener. It does nothing at all unless the drawer is open.
 */
export const useLayoutShell = () => {
    const { isMobile } = useLayout()
    const route        = useRoute()

    /** What `body` had before the drawer took it — restored verbatim, not blanked. */
    let bodyOverflow: string | null = null

    const lockBody = (): void => {
        if (bodyOverflow === null) {
            bodyOverflow = document.body.style.overflow
        }

        document.body.style.overflow = 'hidden'
    }

    const unlockBody = (): void => {
        if (bodyOverflow === null) {
            return
        }

        document.body.style.overflow = bodyOverflow
        bodyOverflow = null
    }

    const onKeydown = (event: KeyboardEvent): void => {
        if (event.key === 'Escape' && menuOpen.value) {
            closeMenu()
        }
    }

    // Growing past the breakpoint: the sidebar is the layout's own column again, so the flag
    // that made it an overlay has to go with it.
    watch(isMobile, (mobile): void => {
        if (!mobile) {
            closeMenu()
        }
    })

    // Navigating is the most common way out of the menu — the row that was tapped has already
    // done its job. `Sidebar.vue` closes on select too; this catches every other route change
    // (a redirect, the back button, a link inside the page). It watches the FULL path, not the
    // path alone, so a query change counts too.
    watch(() => route.fullPath, (): void => {
        closeMenu()
    })

    watch([menuOpen, isMobile], ([open, mobile]): void => {
        if (open && mobile) {
            lockBody()
        } else {
            unlockBody()
        }
    })

    onMounted((): void => {
        document.addEventListener('keydown', onKeydown)
    })

    onBeforeUnmount((): void => {
        document.removeEventListener('keydown', onKeydown)
        unlockBody()
    })
}
